import { useEffect, useRef, useState, type ReactNode } from "react";
import { Icon } from "./Icon";
import { ApiError } from "@/lib/api/errors";

/**
 * Tasdiq dialogi.
 *
 * dev_spec.md §11.2: «destructive action confirmation va reason
 * talab qiladi». Sabab MAJBURIY qilinganida u serverga ham
 * yuboriladi va auditda qoladi — «kim va nega» savoli javobsiz
 * qolmaydi.
 *
 * Shisha yuza: dialog ekranda yakka va qisqa vaqt turadi, shuning
 * uchun `backdrop-filter` narxi o'zini oqlaydi.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Tasdiqlash",
  danger,
  requireReason,
  reasonLabel = "Sabab",
  reasonPlaceholder = "Nima uchun?",
  busy,
  error,
  extra,
  confirmDisabled,
  wide,
  card,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  requireReason?: boolean;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  busy?: boolean;
  error?: unknown;
  extra?: ReactNode;
  /** `extra` dagi majburiy maydon to'ldirilmagan. */
  confirmDisabled?: boolean;
  /** Forma dialogi (`.dialog-wide`). */
  wide?: boolean;
  /**
   * Karta dialogi (`.dialog-card`) — formadan ham keng.
   *
   * Tovar kartasida rasm, 1C maydonlari va ombordagi qoldiq
   * jadvali yonma-yon turadi: 560 pikselda ular ustma-ust
   * tushib, karta uzun lentaga aylanardi.
   */
  card?: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setReason("");
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  if (!open) return null;

  const blocked = requireReason && reason.trim().length === 0;
  const message =
    error instanceof ApiError ? error.body.message : error ? String(error) : null;

  return (
    <dialog
      ref={dialogRef}
      className={`dialog glass-thick${card ? " dialog-wide dialog-card" : wide ? " dialog-wide" : ""}`}
      onCancel={onClose}
    >
      <div className="dialog-head">
        <span className={danger ? "dialog-icon danger" : "dialog-icon"}>
          <Icon name={danger ? "alert" : "check"} size={20} />
        </span>
        <div>
          <div className="dialog-title">{title}</div>
          {description && <div className="dialog-desc">{description}</div>}
        </div>
      </div>

      {/* Forma qismi ALOHIDA suriladi: butun dialog surilganda
          «Tasdiqlash» tugmasi uzun formada ko'rinmay qolardi va
          foydalanuvchi uni qidirib pastga aylantirishi kerak
          bo'lardi. */}
      {extra && <div className="dialog-body">{extra}</div>}

      {requireReason && (
        <label className="field">
          <span className="field-label">{reasonLabel}</span>
          <textarea
            className="input"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={reasonPlaceholder}
            autoFocus
          />
          {/* Sabab serverga yuboriladi va auditda qoladi. */}
          <span className="field-hint">Sabab audit jurnalida saqlanadi.</span>
        </label>
      )}

      {message && (
        <div className="dialog-error" role="alert">
          <Icon name="alert" size={16} />
          <span>{message}</span>
        </div>
      )}

      <div className="dialog-actions">
        <button type="button" className="btn btn-text" onClick={onClose} disabled={busy}>
          Bekor qilish
        </button>
        <button
          type="button"
          className={danger ? "btn btn-danger" : "btn btn-filled"}
          onClick={() => onConfirm(reason.trim())}
          disabled={busy || blocked || confirmDisabled}
        >
          {busy ? "Bajarilmoqda…" : confirmLabel}
        </button>
      </div>
    </dialog>
  );
}
